/**
 * `ChartLightbox` — the one dialog every `ChartFrame` expands into.
 *
 * Mounted **once**, next to `<ChartTip/>` in the app shell, and drawn only while a chart is
 * open (the store holds `null` otherwise, and this renders nothing). A portal on
 * `document.body` keeps it above every panel's stacking and clipping context.
 *
 * A modal in the full sense: `role="dialog"` with `aria-modal`, focus moved to the close
 * button on open and kept inside while open (Tab and Shift+Tab wrap), Escape / the close
 * button / a click on the backdrop close it, and focus returns to the expand button that
 * opened it. Escape is caught at the window in the capture phase so it closes this dialog
 * and nothing under it — the Overview is itself an overlay that Escape would otherwise
 * close as well.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../lib/icons';
import { ChartAxisContext } from './axisContext';
import {
  LIGHTBOX_MAX_ZOOM,
  LIGHTBOX_ZOOM,
  closeChartLightbox,
  lightboxSize,
  readChartLightbox,
  subscribeChartLightbox,
  type ChartLightboxState,
} from './chartLightboxStore';
import { dedupeLightboxIds } from './lightboxIds';
import { ChartZoomContext } from './useChartWidth';

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function viewport() {
  return { vw: window.innerWidth, vh: window.innerHeight };
}

function Dialog({ state }: { state: ChartLightboxState }) {
  const { t } = useTranslation();
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);
  const chart = useRef<HTMLDivElement>(null);
  const legend = useRef<HTMLDivElement>(null);
  const [vp, setVp] = useState(viewport);

  useEffect(() => {
    const onResize = () => setVp(viewport());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Focus the close button on open: the first control, and the one a keyboard reader wants.
  useEffect(() => {
    closeBtn.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        closeChartLightbox();
        return;
      }
      if (e.key !== 'Tab' || !dialog.current) return;
      const nodes = [...dialog.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (nodes.length === 0) return;
      const first = nodes[0]!;
      const last = nodes[nodes.length - 1]!;
      const active = document.activeElement;
      const inside = active instanceof Node && dialog.current.contains(active);
      if (!inside) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  const size = lightboxSize(vp.vw, vp.vh);

  /*
    A chart with a fixed viewBox (no `w` prop) fills the chart area by scaling, and a
    394-unit chart scaled to 1,400 px would print its 10 px labels at 35 px. Cap each such
    SVG at LIGHTBOX_MAX_ZOOM times its own width; charts that were re-laid-out at the
    lightbox width (`chartW`) are never near the cap.
  */
  useLayoutEffect(() => {
    const host = chart.current;
    if (!host) return;
    for (const svg of host.querySelectorAll<SVGSVGElement>('svg[viewBox]')) {
      if (svg.parentElement?.closest('svg')) continue;
      // A chart that measures its own box (#749) is laid out for this width already.
      if (svg.hasAttribute('data-fluid')) continue;
      const width = Number(svg.getAttribute('viewBox')?.split(/[\s,]+/)[2]);
      if (Number.isFinite(width) && width > 0) svg.style.maxWidth = `${Math.round(width * LIGHTBOX_MAX_ZOOM)}px`;
    }
  });

  // The second copy of the chart and its legend: no id or test id on the page twice.
  useLayoutEffect(() => {
    dedupeLightboxIds(chart.current, dialog.current);
    dedupeLightboxIds(legend.current, dialog.current);
  });

  return (
    <div
      className="chart-lightbox-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeChartLightbox();
      }}
    >
      <div
        ref={dialog}
        className="chart-lightbox"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="chart-lightbox"
      >
        <div className="chart-lightbox-head">
          <div className="chart-lightbox-titles">
            <h2 id={titleId} className="chart-lightbox-title">
              {state.title}
            </h2>
            {state.subtitle ? <p className="chart-lightbox-sub">{state.subtitle}</p> : null}
          </div>
          <button
            ref={closeBtn}
            type="button"
            className="chart-lightbox-close"
            aria-label={t('charts.close')}
            title={t('charts.close')}
            data-testid="chart-lightbox-close"
            onClick={() => closeChartLightbox()}
          >
            <Icon name="close" size={18} />
          </button>
        </div>
        <div className="chart-lightbox-body">
          <div
            ref={chart}
            className="chart-lightbox-chart"
            /* The chart area is `size.w` wide; every SVG in it is held to `size.h` tall
               (`--lb-h`, base.css), scaling down to fit rather than being cut off. */
            style={{ width: size.w, maxWidth: '100%', ['--lb-h' as string]: `${size.h}px` }}
            data-w={size.w}
            data-h={size.h}
          >
            <ChartAxisContext.Provider value={{ xLabel: state.xLabel, yLabel: state.yLabel }}>
              {/* Charts that measure their box lay out at its width over this zoom, so
                  their text is drawn larger in the dialog than on the page (#749). */}
              <ChartZoomContext.Provider value={LIGHTBOX_ZOOM}>
                {state.render({ w: size.w, h: size.h, expanded: true })}
              </ChartZoomContext.Provider>
            </ChartAxisContext.Provider>
          </div>
          {state.legend ? <div ref={legend} className="chart-lightbox-legend">{state.legend}</div> : null}
        </div>
      </div>
    </div>
  );
}

export function ChartLightbox() {
  const state = useSyncExternalStore(subscribeChartLightbox, readChartLightbox, readChartLightbox);
  // The portal target only exists after the first client render (see `ChartTip`).
  const [host, setHost] = useState<HTMLElement | null>(null);
  useEffect(() => setHost(document.body), []);

  if (!host || !state) return null;
  // Keyed by the owning frame: a second chart opened over the first is a fresh dialog, so
  // focus moves to its close button again.
  return createPortal(<Dialog key={state.key} state={state} />, host);
}

export default ChartLightbox;
